import { asRecord } from "@/lib/voice/payload";

/** Connection and turn-taking phases surfaced by the voice surfaces. */
export type VoiceStatus = "connecting" | "error" | "idle" | "listening" | "speaking";

/** Every phase except the terminal failure state. */
export type VoiceActiveStatus = Exclude<VoiceStatus, "error">;

/** Who produced a transcript turn. */
export type VoiceRole = "assistant" | "operator";

/** A single transcribed turn, streamed in deltas until it is finalized. */
export interface VoiceTurn {
  id: string;
  /** Original message channel when known; unknown provenance stays literal. */
  channel?: "text" | "voice";
  isFinal: boolean;
  /** Playback ended because the connection failed while this reply was speaking. */
  isPlaybackIncomplete?: boolean;
  /** The reply ended without a final answer; its text is settled guidance, not a draft. */
  isIncomplete?: boolean;
  role: VoiceRole;
  text: string;
}

interface VoiceConversationBase {
  turns: VoiceTurn[];
}

/**
 * A session either carries a failure message or a healthy status, never both.
 * The `errorMessage?: never` arm turns "speaking orb above a red error banner"
 * into a compile error instead of a rule every provider fold must remember.
 */
export type VoiceConversationState =
  | (VoiceConversationBase & {
      errorMessage?: never;
      status: VoiceActiveStatus;
    })
  | (VoiceConversationBase & { errorMessage: string; status: "error" });

export const idleVoiceConversation: VoiceConversationState = {
  status: "idle",
  turns: [],
};

/** Parses a provider event payload, ignoring anything that is not a JSON object. */
export function parseVoiceEvent(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== "string") {
    return null;
  }
  try {
    return asRecord(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Moves the session into its terminal failure state, keeping the transcript. */
export function voiceConversationFailure(
  state: VoiceConversationState,
  message: string,
): VoiceConversationState {
  return {
    errorMessage: message,
    status: "error",
    turns:
      state.status === "speaking"
        ? markLatestAssistantPlaybackIncomplete(state.turns)
        : state.turns,
  };
}

/**
 * Applies a provider status transition. Events that arrive after the session
 * failed or was stopped are ignored, so a late audio frame cannot resurrect a
 * dead session or hide an error banner behind a healthy-looking orb.
 */
export function advanceVoiceStatus(
  state: VoiceConversationState,
  status: VoiceActiveStatus,
): VoiceConversationState {
  if (state.status === "error" || state.status === "idle") {
    return state;
  }
  return { status, turns: state.turns };
}

/** Replaces the transcript without disturbing the status or failure message. */
export function withVoiceTurns(
  state: VoiceConversationState,
  turns: VoiceTurn[],
): VoiceConversationState {
  if (turns === state.turns) {
    return state;
  }
  return state.status === "error"
    ? { errorMessage: state.errorMessage, status: "error", turns }
    : { status: state.status, turns };
}

/**
 * Appends a streamed delta to the matching turn or finalizes it, keeping turn
 * ids stable so the transcript never reorders.
 *
 * Finality is monotonic. Providers stream deltas and the completed turn on the
 * same id, and a delta can arrive after the completed frame; without the latch
 * that late delta would re-open a finished turn and concatenate its text onto
 * the already-final transcript, so the operator sees one utterance duplicated.
 */
/**
 * Folds one delta into an existing turn. A final delta replaces the text (or
 * keeps it when the frame carries none); a non-final delta appends. Finality
 * only ever latches on.
 */
function mergedTurn(existing: VoiceTurn, incoming: VoiceTurn): VoiceTurn {
  return {
    ...existing,
    isFinal: existing.isFinal || incoming.isFinal,
    text: incoming.isFinal ? incoming.text || existing.text : `${existing.text}${incoming.text}`,
  };
}

export function mergeVoiceTurn(turns: VoiceTurn[], incoming: VoiceTurn): VoiceTurn[] {
  const existing = turns.find((turn) => turn.id === incoming.id);
  if (!existing) {
    return incoming.text ? [...turns, incoming] : turns;
  }
  if (existing.isFinal && !incoming.isFinal) {
    return turns;
  }

  return turns.map((turn) => (turn.id === incoming.id ? mergedTurn(turn, incoming) : turn));
}

/**
 * Rewrites the newest turn for a role, used when a provider corrects itself.
 * A correction with no text leaves the transcript alone rather than blanking
 * the turn it refers to.
 */
export function replaceLatestVoiceTurn(
  turns: VoiceTurn[],
  role: VoiceRole,
  text: string,
): VoiceTurn[] {
  if (!text) {
    return turns;
  }

  // `findLastIndex` would read better but needs lib es2023; this project
  // targets ES2022, so the role projection gets the same answer from a built-in.
  const index = turns.map((turn) => turn.role).lastIndexOf(role);

  if (index === -1) {
    return [...turns, { id: `${role}-0`, isFinal: true, role, text }];
  }
  return turns.map((turn, position) =>
    position === index ? { ...turn, isFinal: true, text } : turn,
  );
}

/**
 * Preserves the last assistant reply while making a mid-playback disconnect
 * visible. Reapplying the marker is a no-op so repeated teardown paths do not
 * churn transcript state.
 */
export function markLatestAssistantPlaybackIncomplete(turns: VoiceTurn[]): VoiceTurn[] {
  const index = turns.map((turn) => turn.role).lastIndexOf("assistant");
  if (index === -1 || turns[index].isPlaybackIncomplete) {
    return turns;
  }
  return turns.map((turn, position) =>
    position === index ? { ...turn, isPlaybackIncomplete: true } : turn,
  );
}

/** Returns a stable id for the next turn of a role. */
export function nextVoiceTurnId(turns: VoiceTurn[], role: VoiceRole): string {
  return `${role}-${turns.filter((turn) => turn.role === role).length}`;
}

/** Moves a speaking session back to listening once provider audio has drained. */
export function markVoiceListening(state: VoiceConversationState): VoiceConversationState {
  return state.status === "speaking" ? { status: "listening", turns: state.turns } : state;
}
